/** People pieces shared across pages: avatar initials and filter chips. */
import type { ReactNode } from "react";

import { cn } from "@/lib/cn";

/** "Asha Rao" -> "AR"; falls back to the email's name part. */
export function initials(name: string | null | undefined, email?: string): string {
  const base = (name?.trim() || email?.split("@")[0] || "?").trim();
  const words = base.split(/[\s._-]+/).filter(Boolean);
  if (words.length >= 2) return ((words[0]?.[0] ?? "") + (words[1]?.[0] ?? "")).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

const AVATAR_TONES = [
  "bg-tint-violet text-[#6d4fd8] dark:text-[#b39dff]",
  "bg-brand-soft text-brand",
  "bg-up-soft text-up",
  "bg-tint-peach text-accent",
] as const;

/** A round avatar with initials; the tint follows the id so it stays put. */
export function Avatar({
  id,
  name,
  email,
  className,
}: {
  id: number;
  name: string | null | undefined;
  email?: string;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "grid size-9 shrink-0 place-items-center rounded-full text-xs font-bold",
        AVATAR_TONES[Math.abs(id) % AVATAR_TONES.length],
        className,
      )}
    >
      {initials(name, email)}
    </span>
  );
}

/** A rounded filter chip with an optional count ("Investors 1,041"). */
export function Chip({
  on,
  onClick,
  children,
  count,
  disabled,
  label,
  size = "md",
  tone = "ink",
}: {
  on: boolean;
  onClick: () => void;
  children: ReactNode;
  count?: string | null;
  disabled?: boolean;
  /** An accessible name when the visible text alone isn't enough. */
  label?: string;
  /** "sm": a compact on/off chip inside a table cell; "xs": tighter still (long admin lists). */
  size?: "md" | "sm" | "xs";
  /** "brand": on = brand blue, off = a light outline, disabled = clearly greyed out (Admin → Users). */
  tone?: "ink" | "brand";
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border font-medium whitespace-nowrap transition-colors",
        "focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:cursor-not-allowed",
        size === "xs" ? "h-6 px-2 text-2xs" : size === "sm" ? "h-7 px-2.5 text-2xs" : "h-8 px-3 text-xs",
        tone === "brand"
          ? on
            ? "border-transparent bg-brand text-brand-fg enabled:hover:opacity-90 disabled:bg-surface-3 disabled:text-muted"
            : "border-border bg-transparent text-muted enabled:hover:border-brand enabled:hover:text-fg disabled:border-dashed disabled:opacity-50"
          : cn(
              "disabled:opacity-60",
              on
                ? "border-transparent bg-ink text-ink-fg dark:bg-brand dark:text-brand-fg"
                : "border-border bg-surface text-muted enabled:hover:bg-surface-2 enabled:hover:text-fg",
            ),
      )}
    >
      {children}
      {count != null ? <span className="num opacity-75"> {count}</span> : null}
    </button>
  );
}
